import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { OnDestroyObservable } from '@app/util/lifecycle-hooks/lifecycle-hooks';
import { concat, fromEvent, merge, Observable, race, timer } from 'rxjs';
import { map, shareReplay, takeUntil } from 'rxjs/operators';
import { VITAPEN_MEASUREMENT_END, VITAPEN_MEASUREMENT_START, VITAPEN_TEST_END, VITAPEN_TEST_RESULTS, VITAPEN_TEST_START } from './interfaces';

/**
 * Beobachtet den TappingPen Status
 */
@Injectable({
	providedIn: 'root'
})
export class TappingPenMonitorService {

	static readonly WAIT_TIME = new InjectionToken('WAIT_TIME');
	static readonly DEFAULT_WAIT_TIME = 10000; // 10 Sekunden

	/**
	 * TappingPen Status
	 */
	public readonly active$: Observable<boolean>;

	@OnDestroyObservable
	private onDestroy: Observable<never>;

	constructor(
		window: Window,
		@Inject(TappingPenMonitorService.WAIT_TIME) @Optional() waitTime: number,
	) {
		// Alle TappingPen Events
		const events = merge(
			fromEvent(window, VITAPEN_TEST_START),
			fromEvent(window, VITAPEN_TEST_END),
			fromEvent(window, VITAPEN_TEST_RESULTS),
			fromEvent(window, VITAPEN_MEASUREMENT_START),
			fromEvent(window, VITAPEN_MEASUREMENT_END),
		);

		// Generiertes Event nach waitTime, danach alle TappingPen Events
		const timerPlusEvents = concat(
			timer(waitTime || TappingPenMonitorService.DEFAULT_WAIT_TIME).pipe(
				map(() => new Event(VITAPEN_TEST_END)),
			),
			events,
		);

		// race: Das erste Observable, das Werte liefert wird verwendet. Wenn innerhalb von waitTime keine Events kommen,
		// dann ist das generierte VITAPEN_TEST_END das erste Event und "timerPlusEvents" wird verwendet. Wenn das erste Event
		// innerhalb von waitTime kommt, dann wird "events" verwendet.
		this.active$ = race(
			events,
			timerPlusEvents,
		).pipe(
			map(event => event.type !== VITAPEN_TEST_END),
			takeUntil(this.onDestroy),
		).pipe(
			shareReplay({ bufferSize: 1, refCount: false }),
		);

		// Direktes subscribe. Der Service soll immer auf Events warten
		this.active$.subscribe();
	}
}
